import { EventTypes } from 'redux-segment';

import { LT_FETCH } from 'bl/loggers.bl';
import FLAG from 'config/flag.singleton';
import SENTRY from 'config/sentry.singleton';
import UMSG from 'config/umsg.singleton';
import CT from 'constants/contentType.const';
import HDR from 'constants/httpHeader.const';
import { NO_MEDIA_TYPE } from 'constants/misc.const';
import UMSG_TYPE from 'constants/umsg/type.const';
import { BeAbortError, NoMainTokenError } from 'errors/be.error';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectLockToken } from 'selectors/simple';
import callMock from 'utils/backend/callMock.util';
import fetcher from 'utils/backend/fetcher.util';
import inspectError from 'utils/backend/inspectError.util';
import inspectResponse from 'utils/backend/inspectResponse.util';
import prepareBody from 'utils/backend/prepareBody.util';
import { fromStoreToToken } from 'utils/cognito.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToUmsgType from 'utils/converters/umsg/fromAnyToUmsgType.util';
import { nowAsZulu } from 'utils/date/now.util';
import asFunction from 'utils/functions/asFunction.util';
import withCachedPromise from 'utils/functions/withCachedPromise.util';
import logger from 'utils/logger.util';
import parseJson from 'utils/parsers/parseJson';
import isFn from 'utils/predicates/function/isFn.util';
import replaceSalonId from 'utils/strings/replaceSalonId.util';

const MTAG = 'beV3';
const LOGGER = 'callerV3';
const V = 3;

const L = logger(['beVersion3', LT_FETCH]);

// expected fields for in-progress and done fetches
const PROG = Object.freeze({ MTAG, isLoading: true, isLoaded: false, isFetching: true });
const DONE = Object.freeze({ MTAG, isLoading: false, isLoaded: true, isFetching: false });

// common field in several invocations of next
const META = Object.freeze(FLAG.trackBeCalls ? { analytics: EventTypes.track } : void 1);

const isMessageNeeded = (options) => {
  const isBad = false === options?.is?.ok || false === options?.result?.is?.ok;
  if (!isBad) return false;

  const umsgType = options?.umsgType;
  const result = UMSG_TYPE.global === umsgType || UMSG_TYPE.fn === umsgType;

  L.debug('isMessageNeeded()', options, '->', umsgType, '->', result);
  return result;
};

const callBe = withCachedPromise(async (options) => {
  const { method, base, path, headers, body, salonId, timeout } = options ?? {};

  let text = void 1;
  let json = void 1;
  let is = void 1;
  let status = void 1;
  let ctype = void 1;

  try {
    const response = await fetcher({ v: V, url: `${base}${path}`, headers, method, body, timeout });

    is = inspectResponse(response);
    ctype = is.json ? CT.json : CT.text;

    // eslint-disable-next-line prefer-destructuring
    status = response.status;
    if (is.noContent) return { response, ctype, is, status };

    text = await response.text();
    json = is.json ? parseJson(text) : void 1;

    SENTRY.becall({ logger: LOGGER, v: V, salonId, status, method, base, path, text, json });

    return { response, ctype, is, status, text, json };
  } catch (e) {
    SENTRY.error({
      logger: LOGGER,
      v: V,
      tags: { salonId, method, base, path },
      extra: { status, text, json, ctype, is, timeout },
      error: e,
    });
    throw e;
  }
});

const beVersion3 = async (options) => {
  const ATAG = fromAnyToTrimmed(options?.action?.ATAG);
  const next = asFunction(options?.next);
  const store = options?.store ?? {};

  const { auth, atype: at, base, ctype, pin, data: originalData, params, mock, umsg, umsgType: umsgt, fromDto, toDto, timeout } = options?.action ?? {};
  const { headers: ogHeaders, method, path: ogPath } = options?.action ?? {};

  const umsgType = umsgt ?? fromAnyToUmsgType(umsg);

  try {
    const state = store.getState();
    const lt = selectLockToken(state);

    // simplistic way of replacing missing salonId with most likely valid value
    // SEE: src/utils/backend/endpoint.util.js
    const salonId = selectCurrentSalonId(state);
    const path = replaceSalonId({ path: ogPath, salonId });

    if (pin && !lt) {
      const error = new BeAbortError({ method, path, why: 'pin-unsafe' });
      L.debug('()', error.message);
      return void next({ ATAG, ...DONE, type: at?.fail, error, params, meta: META });
    }

    const headers = { [HDR.ct]: 'application/json', ...(ogHeaders ?? {}), [HDR.lt]: lt };
    // NOTE: options explicitly require no default content type set by this
    if (NO_MEDIA_TYPE === headers[HDR.ct]) delete headers[HDR.ct];

    if (auth) {
      const mt = fromStoreToToken(store);
      headers[HDR.mt] = `Bearer ${mt}`;

      if (!mt) return next({ ATAG, ...DONE, type: at?.fail, error: new NoMainTokenError(), params, meta: META });
    }

    next({ ATAG, ...PROG, type: at?.init, params });

    const m = method || 'GET';
    const call = isFn(mock) ? callMock(mock) : callBe;

    const transformedData = toDto ? toDto(originalData) : originalData;
    const { body } = prepareBody({ data: transformedData, ctype, method: m });
    const originalBody = toDto ? prepareBody({ data: originalData, ctype, method: m }).body : body;

    const result = await call({ v: 3, method: m, base, path, headers, body, salonId, timeout });

    const originalJson = result.json;
    const json = fromDto ? fromDto(originalJson) : originalJson;
    const bodies = { originalBody, originalJson, body, json };
    const receivedAt = nowAsZulu();

    const optionsNext = { ATAG, ...DONE, type: at?.pass, params, meta: META, receivedAt, ...result, ...bodies };
    const optionsUmsg = { ...optionsNext, action: options?.action, salonId, umsgType, umsg };

    if (isMessageNeeded({ umsgType, result })) UMSG.bePass(optionsUmsg);
    return next(optionsNext);
    //
  } catch (error) {
    //
    L.warn('()', error);

    const is = inspectError(error);
    const optionsNext = { ATAG, ...DONE, type: at?.fail, is, error, params, meta: META };
    const optionsUmsg = { ...optionsNext, action: options?.action, umsgType, umsg };

    if (isMessageNeeded({ umsgType, is })) UMSG.beFail(optionsUmsg);
    next(optionsNext);

    throw error;
    //
  } finally {
    next({ ATAG, ...DONE, type: at?.done, params });
  }
};

beVersion3.MTAG = MTAG;

// noinspection JSUnusedGlobalSymbols
export default beVersion3;
